import type { QueryFunctionContext, QueryKey } from '@tanstack/react-query';

import { useInfiniteQueryWithCursor } from '../../../ReactQuery/index';
import { get } from '../../../redux/api';
import type { OIDCProviderScope, OIDCProviderWithTeamName } from '../../../../shared/model-typings/oidcprovider';
import type { CursorPaginatedResponse } from '../../../../shared/types/routes/pagination';

type OrgFilter = {
  scope: OIDCProviderScope.ORG
};
type TeamFilter = {
  scope: OIDCProviderScope.TEAM
  scopeId: number;
};
export type Filter = OrgFilter | TeamFilter;

/**
 * Fetch OIDC providers with cursor pagination
 */
function fetchOIDCProviders(
  { pageParam }: QueryFunctionContext<QueryKey, number | undefined>,
  pageSize: number,
  filter?: Filter,
) {
  const cursor = pageParam ?? 0;
  return get<CursorPaginatedResponse<OIDCProviderWithTeamName, number>>(
    '/api/oidc/providers',
    false,
    {
      pageSize,
      prev: cursor,
      ...(filter && { filter }),
    },
  );
}

export const OIDC_PROVIDERS_QUERY_KEY = 'oidc-providers';

const getQueryKey = (
  pageSize: number,
  filter?: Filter,
): QueryKey => [OIDC_PROVIDERS_QUERY_KEY, pageSize, filter];

/**
 * Hook to fetch OIDC providers with cursor-based infinite pagination.
 * Automatically fetches more data when needed.
 * Caches the data for 5 minutes.
 * @param filter - Optional filter to scope providers by organization or team
 * @param filter.scope - The scope of the filter (organization or team)
 * @param filter.scopeId - The ID of the scope (team ID if scope is team)
 * @param pageSize - Number of providers to fetch per page (default: 50)
 */
export default function useOIDCProviders(
  filter?: Filter,
  pageSize: number = 50,
) {
  return useInfiniteQueryWithCursor<CursorPaginatedResponse<OIDCProviderWithTeamName, number>>({
    queryKey: getQueryKey(pageSize, filter),
    queryFn: (context) => fetchOIDCProviders(context, pageSize, filter),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
